import React from 'react';
import chroma from 'chroma-js';
import { Box, Flex, TextV2, useTheme } from '@withjoy/joykit';

type SnackbarIconProps = Readonly<{ color?: string; size?: number }>;

type SnackbarProps = Readonly<{
  /**
   * The one color the snackbar takes: a theme token (e.g. 'negative1', 'warning1') or any
   * CSS color. Border, icon, and text colors are all derived from it with chroma, so
   * custom colors work for free.
   */
  backgroundColor?: string;
  /**
   * Icon component — not an element. The snackbar renders it with the derived accent
   * color and size itself, so the icon always matches the border by construction.
   */
  icon?: React.ComponentType<SnackbarIconProps>;
  /** Trailing actions (links/buttons), rendered below the message. */
  action?: React.ReactNode;
  children: React.ReactNode;
}>;

const isLight = (color: string): boolean => chroma(color).luminance() > 0.5;

// An unparseable background can't derive anything — fall back to neutral theme tokens
// rather than echoing the background back (a same-color border would be invisible).
const FALLBACK_BORDER = 'mono5';

/**
 * A light background wants a vivid same-hue accent (negative1 → the negative6-family red
 * the palette pairs it with); a light achromatic background has no hue to saturate, so it
 * darkens to a readable gray; a dark background wants white content instead — a same-hue
 * vivid would vanish against it. Callers guard `chroma.valid` before calling.
 */
const deriveAccentColor = (background: string): string => {
  const color = chroma(background);
  if (!isLight(background)) return '#FFFFFF';
  const [hue] = color.hsl();
  if (Number.isNaN(hue)) return color.darken(2.5).hex();
  return chroma.hsl(hue, 0.95, 0.42).hex();
};

const deriveBorderColor = (background: string): string => (isLight(background) ? deriveAccentColor(background) : chroma(background).darken(0.75).hex());

/**
 * The accent color a Snackbar derives from a background — exported so callers can paint
 * their own action content (links, buttons) in the exact color the icon and border use.
 * Accepts the same theme token or CSS color as the `backgroundColor` prop.
 */
export const useSnackbarAccent = (backgroundColor: string): string => {
  const theme = useTheme();
  const resolvedBackground = theme.colors[backgroundColor as keyof typeof theme.colors] ?? backgroundColor;
  return chroma.valid(resolvedBackground) ? deriveAccentColor(resolvedBackground) : theme.colors.mono14;
};

/**
 * Persistent inline notice bar — a snackbar that lives in the layout instead of toasting
 * over it. For transient confirmations keep using `useToast`; this is for state the user
 * should keep seeing while it applies (warnings, misconfigurations, heads-ups).
 */
export const Snackbar = ({ backgroundColor = 'warning1', icon: Icon, action, children }: SnackbarProps) => {
  const theme = useTheme();
  const resolvedBackground = theme.colors[backgroundColor as keyof typeof theme.colors] ?? backgroundColor;
  const isDerivable = chroma.valid(resolvedBackground);
  const accentColor = isDerivable ? deriveAccentColor(resolvedBackground) : theme.colors.mono14;
  const borderColor = isDerivable ? deriveBorderColor(resolvedBackground) : FALLBACK_BORDER;
  const textColor = !isDerivable || isLight(resolvedBackground) ? 'mono14' : 'white';

  return (
    <Flex
      alignItems="flex-start"
      padding={4}
      backgroundColor={resolvedBackground}
      border="1px solid"
      borderColor={borderColor}
      borderRadius={2}
      gap={3}
    >
      {Icon && (
        <Box
          flexShrink={0}
          lineHeight={0}
          style={{ marginTop: 2 }}
        >
          <Icon
            color={accentColor}
            size={16}
          />
        </Box>
      )}
      <Flex
        flexDirection="column"
        alignItems="flex-start"
        gap={2}
        minWidth={0}
      >
        <TextV2
          typographyVariant="label2"
          color={textColor}
        >
          {children}
        </TextV2>
        {action}
      </Flex>
    </Flex>
  );
};
